import { useMemo, useState } from "react";
import { Box, Typography, ButtonBase, Card } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { useTranslation } from "react-i18next";

const VIDEO_ITEMS = [
  {
    id: "what-is-pitchest",
    es: {
      title: "¿Qué es Pitchest?",
      poster: "/assets/home/pitchest-demo-es.webp",
      embed:
        "https://iframe.mediadelivery.net/embed/669182/ed6ad42a-1420-4a95-9a3d-951fc34358ce?autoplay=true&controls=true",
    },
    en: {
      title: "What is Pitchest?",
      poster: "/assets/home/pitchest-demo-en.webp",
      embed:
        "https://player.mediadelivery.net/embed/669182/ba02825a-19f2-4466-9c2c-7bd6b3a5bfec?autoplay=true&controls=true",
    },
  },
  {
    id: "three-things",
    es: {
      title: "3 cosas que tienes que saber",
      poster: "/assets/home/pitchest-3cosas-es.webp",
      embed:
        "https://player.mediadelivery.net/embed/669182/62833ec8-2c92-4d69-b805-a3b27ca09669?autoplay=true&controls=true",
    },
    en: {
      title: "3 things you should know",
      poster: "/assets/home/pitchest-3cosas-en.webp",
      embed:
        "https://player.mediadelivery.net/embed/669182/313e21e0-b99e-431d-b6a5-4794a62c10e8?autoplay=true&controls=true",
    },
  },
  {
    id: "how-to-start",
    es: {
      title: "Cómo empezar",
      poster: "/assets/home/pitchest-howto-es.webp",
      embed:
        "https://player.mediadelivery.net/embed/669182/0f23900d-f2e8-4468-af45-519727d0e7ca?autoplay=true&controls=true",
    },
    en: {
      title: "How to start",
      poster: "/assets/home/pitchest-howto-en.webp",
      embed:
        "https://player.mediadelivery.net/embed/669182/0ac2fce2-191a-4954-b1fe-e349e8cba740?autoplay=true&controls=true",
    },
  },
];

export default function HomeVideoShowcase() {
  const { t, i18n } = useTranslation("home");
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const videos = useMemo(
    () => VIDEO_ITEMS.map((item) => ({ ...item[lang], id: item.id })),
    [lang]
  );

  const activeVideo = videos[activeIndex] || videos[0];

  const handleSelectVideo = (index) => {
    setActiveIndex(index);
    setIsPlaying(false);
  };

  return (
    <Card
      sx={{
        backgroundColor: "rgba(20, 12, 37, 0.82)",
        borderRadius: 5,
        border: "1px solid rgba(255,255,255,0.08)",
        p: { xs: 1.5, md: 2 },
        boxShadow: "0 18px 50px rgba(0,0,0,0.25)",
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="overline"
          sx={{ color: "#C084FC", fontWeight: 700, letterSpacing: 1.1 }}
        >
          {t("videos_kicker")}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          {t("videos_title")}
        </Typography>
      </Box>

      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: 320, sm: 420, md: 760 },
          mx: "auto",
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
          backgroundColor: "rgba(8, 7, 14, 0.45)",
          mb: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: { xs: "13 / 10", sm: "5 / 4", md: "4 / 3" },
            backgroundColor: "#12091f",
          }}
        >
          {isPlaying ? (
            <Box
              component="iframe"
              src={activeVideo.embed}
              title={activeVideo.title}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
              sx={{
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          ) : (
            <ButtonBase
              onClick={() => setIsPlaying(true)}
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                display: "block",
              }}
            >
              <Box
                component="img"
                src={activeVideo.poster}
                alt={activeVideo.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(16,10,30,0.08) 0%, rgba(16,10,30,0.28) 100%)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  right: { xs: 18, md: 28 },
                  bottom: { xs: 18, md: 28 },
                  width: { xs: 64, md: 84 },
                  height: { xs: 64, md: 84 },
                  borderRadius: "50%",
                  background:
                    "linear-gradient(180deg, rgba(168,85,247,0.98) 0%, rgba(126,87,194,0.98) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 18px 36px rgba(0,0,0,0.35)",
                  border: "1px solid rgba(255,255,255,0.22)",
                }}
              >
                <PlayArrowRoundedIcon sx={{ fontSize: { xs: 34, md: 42 } }} />
              </Box>
            </ButtonBase>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 1.5,
          mt: 0.5,
        }}
      >
        {videos.map((video, index) => (
          <ButtonBase
            key={video.id}
            onClick={() => handleSelectVideo(index)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              textAlign: "left",
              borderRadius: 3,
              p: 1,
              border:
                index === activeIndex
                  ? "1px solid rgba(168,85,247,0.8)"
                  : "1px solid rgba(255,255,255,0.08)",
              backgroundColor:
                index === activeIndex
                  ? "rgba(124,58,237,0.16)"
                  : "rgba(255,255,255,0.03)",
              justifyContent: "flex-start",
            }}
          >
            <Box
              component="img"
              src={video.poster}
              alt={video.title}
              sx={{
                width: 92,
                height: 56,
                borderRadius: 2,
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.95rem", md: "1rem" },
                color: "#fff",
                lineHeight: 1.2,
              }}
            >
              {video.title}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Card>
  );
}
